import { createContext, useEffect, useReducer, useContext } from "react";
import { createEntityReducer } from "./entityReducer";
import { AuthContext } from "../context/AuthContext";

export function createEntityContext(entityKey, endpoint, slugKey = "slug") {
  const EntityContext = createContext();

  const currentKey = `current${entityKey
    .slice(0, -1)[0]
    .toUpperCase()}${entityKey.slice(1, -1)}`;

  const initialState = {
    [entityKey]: [],
    [currentKey]: null,
    message: "",
    loading: false,
    error: null,
  };

  const EntityProvider = ({ children }) => {
    const { accessToken } = useContext(AuthContext);
    const [state, dispatch] = useReducer(
      createEntityReducer(entityKey),
      initialState
    );

    // Helper to build fetch options dynamically
    const buildFetchOptions = (method, body, isMultipart = false) => {
      const headers = { Authorization: `Bearer ${accessToken}` };

      if (!isMultipart) {
        headers["Content-Type"] = "application/json";
      }

      return {
        method,
        headers,
        credentials: "include",
        body: isMultipart ? body : JSON.stringify(body),
      };
    };

    const fetchEntities = async () => {
      dispatch({ type: "FETCH_START" });
      try {
        const res = await fetch(endpoint);
        const data = await res.json();
        dispatch({ type: "REPLACE_ALL", payload: data });
      } catch {
        dispatch({
          type: "FETCH_ERROR",
          payload: `Failed to load ${entityKey}`,
        });
      }
    };

    useEffect(() => {
      fetchEntities();
    }, []);

    const addEntity = async (entity, { isMultipart = false } = {}) => {
      try {
        const res = await fetch(
          endpoint,
          buildFetchOptions("POST", entity, isMultipart)
        );
        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.message || `Failed to add ${entityKey}`);
        }
        const data = await res.json();
        dispatch({ type: "ADD", payload: data });
      } catch (err) {
        dispatch({ type: "SET_MESSAGE", payload: err.message });
      }
    };

    const updateEntity = async (
      id,
      updatedEntity,
      { isMultipart = false } = {}
    ) => {
      try {
        const res = await fetch(
          `${endpoint}/${id}`,
          buildFetchOptions("PATCH", updatedEntity, isMultipart)
        );
        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.message || `Error updating ${entityKey}`);
        }
        const data = await res.json();
        dispatch({ type: "UPDATE", payload: data });
        fetchEntities();
        if (data[slugKey]) {
          setCurrentBySlug(data[slugKey]);
        }
      } catch (err) {
        dispatch({ type: "SET_MESSAGE", payload: err.message });
      }
    };

    const deleteEntity = async (id) => {
      try {
        const res = await fetch(
          `${endpoint}/${id}`,
          buildFetchOptions("DELETE")
        );
        if (!res.ok) {
          throw new Error(`Error deleting ${entityKey}`);
        }
        dispatch({ type: "DELETE", payload: id });
      } catch (err) {
        dispatch({ type: "SET_MESSAGE", payload: err.message });
      }
    };

    const setCurrentBySlug = (slug) => {
      const match = state[entityKey].find((item) => item[slugKey] === slug);
      dispatch({ type: "SET_CURRENT", payload: match || null });
    };

    return (
      <EntityContext.Provider
        value={{
          ...state,
          fetchEntities,
          addEntity,
          updateEntity,
          deleteEntity,
          setCurrentBySlug,
        }}
      >
        {children}
      </EntityContext.Provider>
    );
  };

  return { EntityContext, EntityProvider };
}
